/**
 * GameEngine - Główny silnik gry Kroniki Królestwa
 * Browser-compatible version (offline game engine)
 *
 * Odpowiedzialny za:
 * - Inicjalizację gry
 * - Setup początkowy (talię, karty startowe, Bazar, Szlaki)
 * - Główne operacje gry (zakup, wyzwanie, podjęcie zlecenia)
 * - Walidację akcji
 */

import ResourceManager from './ResourceManager.js';
import TurnManager from './TurnManager.js';
import CardEffects from './CardEffects.js';
import { ventureDrawCounts, takeNonEventCards, restoreDrawnCards } from './utils.js';
import { splitCardRewards } from '../cardRewardSlots.js';
import { getChallengeGateSurcharge } from '../../utils/pathChallengeUtils.js';

/**
 * Typy efektow traktowane jako Ofiara (dawna Kara) wydrukowana na karcie Przygody.
 * Lustro `GameEngine.PENALTY_EFFECT_TYPES` z backendu.
 */
const OFFLINE_PENALTY_TYPES = [
  'TAKE_WOUND', 'TAKE_CURSE', 'TAKE_SHAME', 'TAKE_WOUND_PER_MISSING_STRENGTH',
  'TAKE_WOUND_IF_NO_TROPHIES',
  'LOSE_GOLD', 'LOSE_KNOWLEDGE', 'LOSE_STRENGTH', 'LOSE_FAME', 'LOSE_MAIN_ACTION',
  'LOSE_ALL_GOLD', 'LOSE_ALL_RESOURCES',
  'SKIP_NEXT_TURN',
  'DISCARD_HAND', 'DISCARD_FROM_HAND', 'DISCARD_AND_DRAW',
  'DESTROY_FROM_HAND', 'DESTROY_EQUIPPED_ARTIFACT',
  'ALL_OTHER_PLAYERS_TAKE_WOUND', 'ALL_PLAYERS_LOSE_GOLD',
  'ALL_PLAYERS_LOSE_FAME', 'ALL_PLAYERS_TAKE_SHAME', 'ALL_PLAYERS_DISCARD'
];
// Static JSON imports — bundled, available offline without fetch
import cardsBgsPl from '../../public/assets/card_bgs/cards_bgs_pl.json';
import cardsBgsEn from '../../public/assets/card_bgs/cards_bgs_en.json';
import cardsBgsDe from '../../public/assets/card_bgs/cards_bgs_de.json';

const CARDS_BGS_BY_LOCALE = { pl: cardsBgsPl, en: cardsBgsEn, de: cardsBgsDe };
let currentLocale = 'pl';
export function setGameEngineLocale(locale) { currentLocale = locale; }
function getCardsData() { return CARDS_BGS_BY_LOCALE[currentLocale] || cardsBgsPl; }

const HeroService = {
  applyHeroPassive: async () => ({}),
  getHeroById: async () => null,
  applyPassiveAbility: async () => ({}),
  getStarterDeckForFaction: async (faction) => {
    const starterCards = getCardsData().cards?.starter || [];
    const factionStarters = starterCards.filter(c => c.faction === faction);
    const deck = [];
    factionStarters.forEach(c => {
      for (let i = 0; i < (c.count || 1); i++) deck.push(c.id);
    });
    return deck;
  },
  useActiveAbility: async () => ({ success: false }),
  checkPassiveTriggers: async () => []
};

const GameConfigService = {
  getDefaultConfig: async () => ({
    handSize: 5,
    bankResourceLimit: 10,
    marketplaceSize: 6,
    pathSize: 4,
    mainActionsPerTurn: 2,
    purchasesPerTurn: 1,
    challengesPerTurn: 1,
    epicChallengesForWin: 2,
    guildContractsRevealed: 3,
    maxActiveContracts: 2
  }),
  getConfig: async () => ({
    handSize: 5,
    bankResourceLimit: 10,
    marketplaceSize: 6,
    pathSize: 4,
    mainActionsPerTurn: 2,
    purchasesPerTurn: 1,
    challengesPerTurn: 1,
    epicChallengesForWin: 2,
    guildContractsRevealed: 3,
    maxActiveContracts: 2
  })
};

const TUTORIAL_CARD_LAYOUT = {};

// In-memory cache for cards (per locale)
let cachedAllCards = null;
let cachedAllCardsLocale = null;

class GameEngine {
  /**
   * Załaduj karty z cards_bgs_{locale}.json (używa statycznego importu — działa offline)
   * @returns {Array} - Tablica wszystkich kart (spłaszczona z kategorii)
   */
  static async loadCards() {
    if (cachedAllCards && cachedAllCardsLocale === currentLocale) return cachedAllCards;
    const allCards = Object.values(getCardsData().cards || {}).flat();
    cachedAllCards = allCards;
    cachedAllCardsLocale = currentLocale;
    return allCards;
  }

  /**
   * Normalizuje koszt karty do formatu { gold, strength, knowledge }
   * Obsługuje różne formaty kosztu z cards.json
   * @param {Object} card - Pełny obiekt karty
   * @returns {Object} - Znormalizowany koszt
   */
  static normalizeCardCost(card) {
    // Jeśli karta ma już obiekt cost, użyj go
    if (card.cost && typeof card.cost === 'object') {
      return card.cost;
    }

    // Jeśli cost to liczba, traktuj jako złoto (dla kart z Targowiska)
    if (typeof card.cost === 'number') {
      return { gold: card.cost };
    }

    // Dla potworów - użyj requiredStrength z stats
    if (card.type === 'monster' || card.type === 'epic_boss') {
      const requiredStrength = card.stats?.requiredStrength || card.difficulty || 0;
      return { strength: requiredStrength };
    }

    // Dla pułapek - użyj requiredKnowledge z stats
    if (card.type === 'trap' || card.type === 'event' || card.type === 'quest' || card.type === 'epic_quest') {
      const requiredKnowledge = card.stats?.requiredKnowledge || card.difficulty || 0;
      return { knowledge: requiredKnowledge };
    }

    // Brak kosztu
    return {};
  }

  /**
   * Pobierz kartę po ID
   * @param {String} cardId - ID karty
   * @returns {Object|null} - Obiekt karty lub null
   */
  static async getCardById(cardId) {
    // Najpierw szukaj w głównych kartach
    const cards = await this.loadCards();

    let card = cards.find(c => c.id === cardId);

    // Jeśli nie znaleziono, szukaj w kartach startowych
    if (!card) {
      card = await this.getStarterCardById(cardId);
    }

    // Normalizuj koszt karty
    if (card) {
      card = { ...card, cost: this.normalizeCardCost(card) };
    }

    return card;
  }

  /**
   * Pobierz kartę startową po ID
   * @param {String} cardId - ID karty startowej
   * @returns {Object|null} - Obiekt karty lub null
   */
  static async getStarterCardById(cardId) {
    const data = loadStarterCardData();
    for (const factionCards of Object.values(data)) {
      const card = Array.isArray(factionCards) ? factionCards.find(c => c.id === cardId) : null;
      if (card) return card;
    }
    return null;
  }

  /**
   * Przygotuj talię szlaku według specyficznego algorytmu
   *
   * ALGORYTM:
   * 1. Podziel standardowe karty na 3 równe kupki (BEZ tasowania)
   * 2. Potasuj KAŻDĄ kupkę osobno
   * 3. Do każdej kupki wstaw w LOSOWE miejsce po 1 karcie eventu
   * 4. Połącz 3 kupki (kupka1 + kupka2 + kupka3) - BEZ tasowania
   * 5. Podziel na 2 połówki
   * 6. Wstaw kartę specjalną #1 NA SPÓD pierwszej połówki
   * 7. Wstaw kartę specjalną #2 NA SPÓD drugiej połówki
   * 8. Połącz połówki (half1 na wierzchu, half2 na spodzie) - BEZ tasowania
   *
   * @param {Array} standardCards - Standardowe karty szlaku (ID)
   * @param {Array} specialCards - Karty specjalne (Bossy/Epickie Zadania) (ID) - max 2
   * @param {Array} events - Karty wydarzeń (ID) - max 3
   * @returns {Array} - Gotowa talia (ID)
   */
  static preparePathDeck(standardCards, specialCards, events) {
    // 1. Podziel standardowe karty na 3 równe kupki (BEZ wstępnego tasowania całości)
    const thirdSize = Math.ceil(standardCards.length / 3);
    const pile1Raw = standardCards.slice(0, thirdSize);
    const pile2Raw = standardCards.slice(thirdSize, thirdSize * 2);
    const pile3Raw = standardCards.slice(thirdSize * 2);

    // 2. Potasuj KAŻDĄ kupkę osobno (Fisher-Yates)
    const piles = [
      this.shuffleArray(pile1Raw),
      this.shuffleArray(pile2Raw),
      this.shuffleArray(pile3Raw)
    ];

    console.log(`[preparePathDeck] Kupki przed eventami: ${piles[0].length}, ${piles[1].length}, ${piles[2].length}`);

    // 3. Włóż po 1 wydarzeniu NA SPÓD każdej kupki
    // (Dobieramy z góry shift(), więc push() = spód = event pojawi się dopiero po przejściu całej kupki)
    for (let i = 0; i < 3; i++) {
      if (events[i]) {
        piles[i].push(events[i]);
        console.log(`[preparePathDeck] Event ${events[i]} wstawiony na spód kupki ${i + 1} (pozycja ${piles[i].length - 1})`);
      }
    }

    // 4. Połącz kupki w jedną talię (BEZ dodatkowego tasowania!)
    // Kolejność: kupka1 na wierzchu, kupka3 na spodzie
    const combinedDeck = [...piles[0], ...piles[1], ...piles[2]];
    console.log(`[preparePathDeck] Połączona talia: ${combinedDeck.length} kart`);

    // 5. Podziel na 2 połówki
    const halfSize = Math.ceil(combinedDeck.length / 2);
    const half1 = combinedDeck.slice(0, halfSize);
    const half2 = combinedDeck.slice(halfSize);

    // 6. Dodaj karty specjalne NA SPÓD każdej połówki
    // Karty dobieramy z góry (shift), więc push() = na spód
    if (specialCards.length > 0 && specialCards[0]) {
      half1.push(specialCards[0]);
      console.log(`[preparePathDeck] Karta specjalna #1 (${specialCards[0]}) na spodzie pierwszej połówki`);
    }
    if (specialCards.length > 1 && specialCards[1]) {
      half2.push(specialCards[1]);
      console.log(`[preparePathDeck] Karta specjalna #2 (${specialCards[1]}) na spodzie drugiej połówki`);
    }

    // 7. Połącz połówki BEZ TASOWANIA
    // Half1 jest "na wierzchu", Half2 "na spodzie"
    // Dobieramy z góry (shift), więc kolejność: [...half1, ...half2]
    const finalDeck = [...half1, ...half2];
    console.log(`[preparePathDeck] Finalna talia: ${finalDeck.length} kart`);

    return finalDeck;
  }

  /**
   * Inicjalizuj grę - ustaw początkowy stan
   * @param {Object} game - Obiekt gry
   * @returns {Object} - Status operacji
   */
  static async initializeGame(game) {
    try {
      // Zamroź konfigurację gry przy starcie
      const config = await GameConfigService.getConfig();
      game.gameConfig = { ...config };

      const cards = await this.loadCards();

      // 1. Przygotuj pule kart według typów

      // Bazar: sojusznicy, manewry, artefakty NORMALNE (bez legendarnych!)
      // Legendarne artefakty są tylko nagrodami za pokonanie Bossów
      const marketCards = cards.filter(c =>
        ['ally', 'maneuver'].includes(c.type) ||
        (c.type === 'artifact' && c.subtype === 'normal')
      );

      // Legendarne artefakty - osobna talia na nagrody
      const legendaryArtifacts = cards.filter(c =>
        c.type === 'artifact' && c.subtype === 'legendary'
      ).map(c => c.id);

      // Pule specjalne
      const allEvents = cards.filter(c => c.type === 'event').map(c => c.id);

      // Epic Bossy mają type: "monster" i subtype: "boss"
      const allEpicBosses = cards.filter(c =>
        c.type === 'monster' && c.subtype === 'boss'
      ).map(c => c.id);

      // Epic Questy mają type: "epic_quest"
      const allEpicQuests = cards.filter(c => c.type === 'epic_quest').map(c => c.id);

      // Standardowe karty szlaków (BEZ eventów, bossów i specjali!)
      // Ścieżka Męstwa: tylko zwykłe potwory (nie bossy)
      const courageStandard = cards.filter(c =>
        c.type === 'monster' && c.subtype !== 'boss'
      ).map(c => c.id);

      // Ścieżka Mądrości: pułapki i zwykłe zadania
      // UWAGA: epic_quest ma osobny typ "epic_quest", nie "quest"
      // więc NIE trafi do tej puli (analogicznie do bossów na ścieżce męstwa)
      const wisdomStandard = cards.filter(c =>
        ['trap', 'quest'].includes(c.type) && c.type !== 'epic_quest'
      ).map(c => c.id);

      console.log(`[INIT] Pule kart:`);
      console.log(`  - Events: ${allEvents.length}`);
      console.log(`  - Epic Bosses: ${allEpicBosses.length} [${allEpicBosses.slice(0, 2).join(', ')}]`);
      console.log(`  - Epic Quests: ${allEpicQuests.length} [${allEpicQuests.slice(0, 2).join(', ')}]`);
      console.log(`  - Courage Standard (monsters, no bosses): ${courageStandard.length}`);
      console.log(`  - Wisdom Standard (traps/quests, no epic): ${wisdomStandard.length}`);

      // 2. Losowanie kart specjalnych i eventów

      // Losuj 6 eventów (3 na ścieżkę)
      const shuffledEvents = this.shuffleArray(allEvents);
      const wisdomEvents = shuffledEvents.slice(0, 3);
      const courageEvents = shuffledEvents.slice(3, 6);

      // Losuj 2 Bossów na Siłę
      const shuffledBosses = this.shuffleArray(allEpicBosses);
      const courageBosses = shuffledBosses.slice(0, 2);

      // Losuj 2 Epickie Zadania
      const shuffledEpicQuests = this.shuffleArray(allEpicQuests);
      const wisdomEpicQuests = shuffledEpicQuests.slice(0, 2);

      // Talia Gildii (questDeck) - na razie pusta
      const questCards = [];

      // Kontrakty Gildii
      const guildContracts = cards.filter(c => c.type === 'guild_contract').map(c => c.id);
      console.log(`  - Guild Contracts: ${guildContracts.length}`);

      // 3. Budowanie talii
      game.marketDeck = this.shuffleArray(marketCards.map(c => c.id));

      // Budowanie talii Szlaku Męstwa
      game.courageDeck = this.preparePathDeck(
        courageStandard,
        courageBosses,
        courageEvents
      );

      // Budowanie talii Szlaku Mądrości
      game.wisdomDeck = this.preparePathDeck(
        wisdomStandard,
        wisdomEpicQuests,
        wisdomEvents
      );

      game.questDeck = this.shuffleArray(questCards.map(c => c.id));

      // Talia Kontraktów Gildii - tasuj i odkryj 3 pierwsze
      game.guildContractDeck = this.shuffleArray(guildContracts);
      game.guildContractDiscard = [];
      game.revealedGuildContracts = [];
      // Odkryj pierwsze 3 kontrakty
      game.revealGuildContracts();

      // Talia legendarnych artefaktów (nagrody za Bossów)
      game.legendaryArtifactsDeck = this.shuffleArray(legendaryArtifacts);

      // 4. Wypełnij Bazar
      game.marketplace = [];
      for (let i = 0; i < config.marketplaceSize && game.marketDeck.length > 0; i++) {
        game.marketplace.push(game.marketDeck.shift());
      }

      // 5. Wypełnij Szlak Odwagi
      game.pathOfCourage = [];
      for (let i = 0; i < config.pathSize && game.courageDeck.length > 0; i++) {
        game.pathOfCourage.push(game.courageDeck.shift());
      }

      // 6. Wypełnij Szlak Mądrości (3 karty)
      game.pathOfWisdom = [];
      game.wisdomDiscard = game.wisdomDiscard || [];
      for (let i = 0; i < config.pathSize && game.wisdomDeck.length > 0; i++) {
        game.pathOfWisdom.push(game.wisdomDeck.shift());
      }

      // 7. Przygotuj graczy
      for (const player of game.players) {
        // Dla trybu offline przypisz domyślną frakcję/bohatera jeśli brak
        if (!player.faction) {
          const starterCards = getCardsData().cards?.starter || [];
          const allFactions = [...new Set(starterCards.map(c => c.faction).filter(Boolean))];
          player.faction = allFactions[Math.floor(Math.random() * allFactions.length)] || 'zlota_karawana';
        }
        if (!player.heroCard) {
          player.heroCard = { id: `offline_hero_${player.faction}`, name: player.username };
        }

        // Pobierz karty startowe dla frakcji gracza
        const startingDeck = await HeroService.getStarterDeckForFaction(player.faction);
        console.log(`[INIT] Gracz ${player.username} (${player.faction}) - talia startowa:`, startingDeck.length, 'kart');

        // Potasuj talię
        const shuffledDeck = this.shuffleArray(startingDeck);

        // Rozdziel: karty na rękę, reszta do stosu doboru
        const handCards = shuffledDeck.slice(0, config.handSize);
        const deckCards = shuffledDeck.slice(config.handSize);

        console.log(`[INIT] Gracz ${player.username} - ręka:`, handCards.length, 'kart, deck:', deckCards.length, 'kart');

        // Przypisz karty do gracza (używając set dla mongoose arrays)
        player.hand = handCards;
        player.deck = deckCards;
        player.discard = [];
        player.played = [];
        player.destroyed = [];
        // Nowy slotowy system ekwipunku
        player.equippedArtifacts = { pancerz: null, bron: null, amulet: null };
        player.artifactsUsedThisTurn = [];
        player.equipped = [];  // [LEGACY]

        // Definicja zasobów startowych per frakcja
        const factionStartingResources = {
          'zlota_karawana': { gold: 1, strength: 2, knowledge: 0 },
          'horda_pustkowi': { gold: 2, strength: 1, knowledge: 0 },
          'mechanistyczna_kuznia': { gold: 1, strength: 0, knowledge: 0, tokens: { parts: 2 } },
          'cienie_zaulkow': { gold: 1, strength: 0, knowledge: 2 },
          'zakon_srebrnego_gryfa': { gold: 1, strength: 1, knowledge: 1 },
          'krag_zywiolow': { gold: 1, strength: 0, knowledge: 2 },
          'lesni_tropiciele': { gold: 1, strength: 1, knowledge: 1 },
          'potomkowie_aspektu_smoka': { gold: 3, strength: 0, knowledge: 0 },
          'straznicy_gorskiego_bastionu': { gold: 1, strength: 2, knowledge: 0 },
          'krag_pierwotnej_kniei': { gold: 1, strength: 1, knowledge: 1 },
          'wyznawcy_kruczej_krolowej': { gold: 1, strength: 1, knowledge: 1 },
          'zakon_oczyszczajacego_plomienia': { gold: 1, strength: 2, knowledge: 0 }
        };

        const startRes = factionStartingResources[player.faction] || { gold: 1, strength: 1, knowledge: 1 };

        // Resetuj zasoby i liczniki
        player.resources = {
          strength: startRes.strength || 0,
          knowledge: startRes.knowledge || 0,
          gold: startRes.gold || 0
        };

        // Inicjalizuj tokeny (resetuj)
        player.tokens = {
          fury: 0,
          power: 0,
          parts: startRes.tokens?.parts || 0,
          knowledge: startRes.tokens?.knowledge || 0
        };
        player.fame = 0;
        player.questsCompleted = [];

        // Resetuj liczniki tury
        player.mainActionsRemaining = config.mainActionsPerTurn;
        player.bonusActionsThisTurn = 0;
        player.purchasesRemaining = config.purchasesPerTurn;
        player.challengesRemaining = config.challengesPerTurn;

        console.log(`[INIT] Gracz ${player.username} zainicjalizowany - hand:`, player.hand, 'deck:', player.deck);
      }

      // 8. Ustaw stan gry
      game.status = 'active';
      game.currentTurn = 1;
      game.currentPlayerIndex = 0;
      // Od razu ustawiamy fazę ACTION - faza START jest automatycznie obsługiwana w TurnManager.startPhase
      game.currentPhase = TurnManager.PHASES.ACTION;

      // 8b. Ustaw czas rozpoczęcia gry (dla systemu historii)
      game.gameStartedAt = new Date();

      // 8a. Inicjalizuj pola dla systemu wydarzeń
      game.pendingEvent = {
        cardId: null,
        triggeringPlayerId: null,
        triggeringPlayerName: null,
        acknowledged: false,
        effects: [],
        timestamp: null
      };
      game.activeDurationEffects = [];

      // 9. Log
      game.logEvent('SYSTEM', 'System', 'game_initialized', {
        playerCount: game.players.length,
        marketSize: game.marketplace.length,
        pathOfCourageSize: game.pathOfCourage.length,
        pathOfWisdomSize: game.pathOfWisdom.length,
        deckStructure: 'Advanced 3-pile + halves shuffle applied'
      });

      return {
        success: true,
        message: 'Gra zainicjalizowana pomyślnie',
        state: {
          marketplace: game.marketplace,
          pathOfCourage: game.pathOfCourage,
          pathOfWisdom: game.pathOfWisdom,
          players: game.players.map(p => ({
            username: p.username,
            handSize: p.hand.length,
            deckSize: p.deck.length
          }))
        }
      };
    } catch (error) {
      console.error('Błąd inicjalizacji gry:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Inicjalizuj grę tutorial - predefined card layouts (no shuffle)
   * @param {Object} game - Obiekt gry
   * @returns {Object} - Status operacji
   */
  static async initializeTutorialGame(game) {
    try {
      const config = await GameConfigService.getConfig();
      game.gameConfig = { ...config };

      const layout = TUTORIAL_CARD_LAYOUT;

      // Marketplace & deck (predefined, NO shuffle)
      game.marketplace = [...(layout.marketplace || [])];
      game.marketDeck = [...(layout.marketDeck || [])];
      game.marketDiscard = [];

      // Paths (predefined, NO shuffle)
      game.pathOfCourage = [...(layout.pathOfCourage || [])];
      game.courageDeck = [...(layout.courageDeck || [])];
      game.courageDiscard = [];

      game.pathOfWisdom = [...(layout.pathOfWisdom || [])];
      game.wisdomDeck = [...(layout.wisdomDeck || [])];
      game.wisdomDiscard = [];

      // Guild contracts (predefined for tutorial)
      if (layout.guildContracts && layout.guildContracts.length > 0) {
        game.revealedGuildContracts = [...layout.guildContracts];
        game.guildContractDeck = [];
      } else {
        game.guildContractDeck = [];
        game.revealedGuildContracts = [];
      }
      game.guildContractDiscard = [];
      game.legendaryArtifactsDeck = [];
      game.questDeck = [];

      // Player setup
      const player = game.players[0];
      player.hand = [...(layout.startingHand || [])];
      player.deck = [...(layout.startingDeck || [])];
      player.discard = [];
      player.played = [];
      player.destroyed = [];
      player.equippedArtifacts = { pancerz: null, bron: null, amulet: null };
      player.artifactsUsedThisTurn = [];
      player.equipped = [];

      // Starting resources (2 gold as per tutorial)
      player.resources = {
        gold: layout.startingResources?.gold || 0,
        strength: layout.startingResources?.strength || 0,
        knowledge: layout.startingResources?.knowledge || 0
      };
      player.tokens = { fury: 0, power: 0, parts: 0, knowledge: 0 };
      player.fame = 0;
      player.questsCompleted = [];

      // Turn counters
      player.mainActionsRemaining = config.mainActionsPerTurn;
      player.bonusActionsThisTurn = 0;
      player.purchasesRemaining = config.purchasesPerTurn;
      player.challengesRemaining = config.challengesPerTurn;

      // Game state
      game.status = 'active';
      game.currentTurn = 1;
      game.currentPlayerIndex = 0;
      game.currentPhase = TurnManager.PHASES.ACTION;
      game.gameStartedAt = new Date();
      game.bossesDefeated = 0;
      game.epicQuestsCompleted = 0;

      // Events system init
      game.pendingEvent = {
        cardId: null,
        triggeringPlayerId: null,
        triggeringPlayerName: null,
        acknowledged: false,
        effects: [],
        timestamp: null
      };
      game.activeDurationEffects = [];

      game.logEvent('SYSTEM', 'System', 'game_initialized', {
        playerCount: 1,
        isTutorial: true,
        marketSize: game.marketplace.length,
        pathOfCourageSize: game.pathOfCourage.length,
        pathOfWisdomSize: game.pathOfWisdom.length
      });

      console.log(`[TUTORIAL INIT] Tutorial game initialized for ${player.username}`);

      return {
        success: true,
        message: 'Tutorial game initialized',
        state: {
          marketplace: game.marketplace,
          pathOfCourage: game.pathOfCourage,
          pathOfWisdom: game.pathOfWisdom,
          players: [{ username: player.username, handSize: player.hand.length, deckSize: player.deck.length }]
        }
      };
    } catch (error) {
      console.error('Błąd inicjalizacji tutorial:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Mapuj kategorię artefaktu na klucz slotu
   * @param {String} category - Kategoria artefaktu ('Pancerz', 'Broń', 'Amulet' oraz warianty Legendarne)
   * @returns {String} - Klucz slotu ('pancerz', 'bron', 'amulet')
   */
  static mapCategoryToSlot(category) {
    // Legendarne Artefakty mają w danych pełną nazwę kategorii („Legendarny Pancerz”),
    // więc dopasowujemy po rdzeniu, a nie po całym napisie.
    const mapping = {
      'Pancerz': 'pancerz',
      'Broń': 'bron',
      'Amulet': 'amulet',
      'Legendarny Pancerz': 'pancerz',
      'Legendarna Broń': 'bron',
      'Legendarny Amulet': 'amulet'
    };
    return mapping[category] || null;
  }

  /**
   * Fisher-Yates shuffle
   * @param {Array} array - Tablica do potasowania
   * @returns {Array} - Potasowana tablica
   */
  static shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * AKCJA: Zakup karty z Targowiska
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @param {String} cardId - ID karty do zakupu
   * @returns {Object} - Status operacji
   */
  static async purchaseCard(game, player, cardId) {
    try {
      // 1. Sprawdź czy gracz ma akcję główną lub dodatkowy zakup
      const hasActions = (player.extraPurchases || 0) > 0 ||
        (player.hasActionsRemaining ? player.hasActionsRemaining() : (player.mainActionsRemaining + player.bonusActionsThisTurn) > 0);
      if (!hasActions) {
        return { success: false, error: 'Brak dostępnych akcji głównych' };
      }

      // 2. Sprawdź czy karta jest na Targowisku
      const cardIndex = game.marketplace.indexOf(cardId);
      if (cardIndex === -1) {
        return { success: false, error: 'Karta nie znajduje się na Targowisku' };
      }

      // 3. Pobierz dane karty
      const card = await this.getCardById(cardId);
      if (!card) {
        return { success: false, error: 'Nie znaleziono karty w bazie' };
      }

      // 4. Sprawdź koszt i zasoby gracza (pula + bank) z uwzględnieniem efektów wydarzeń
      let cost = { ...this.normalizeCardCost(card) };

      // Zastosuj modyfikatory z aktywnych efektów wydarzeń
      if (['normal_artifact', 'legendary_artifact'].includes(card.type)) {
        // ARTIFACTS_CHEAPER: -X do kosztu artefaktów (efekty wydarzeń)
        if (game.activeDurationEffects && game.activeDurationEffects.length > 0) {
          const discount = game.getDurationEffectTotal('ARTIFACTS_CHEAPER');
          if (discount > 0 && cost.gold !== undefined) {
            cost.gold = Math.max(0, cost.gold - discount);
            console.log(`[PURCHASE] ARTIFACTS_CHEAPER: -${discount} złota (${cost.gold} total)`);
          }
        }
        // NEXT_ARTIFACT_COSTS_4_GOLD_LESS: zniżka z efektu karty gracza
        if (player.artifactDiscount > 0 && cost.gold !== undefined) {
          const discount = player.artifactDiscount;
          cost.gold = Math.max(0, cost.gold - discount);
          player.artifactDiscount = 0;
          console.log(`[PURCHASE] artifactDiscount: -${discount} złota (${cost.gold} total)`);
        }
      }

      // NEXT_PURCHASE_DISCOUNT: ogólna zniżka na następny zakup (np. Ulubiony Klient)
      if (player.nextPurchaseDiscount > 0 && cost.gold !== undefined) {
        const discount = player.nextPurchaseDiscount;
        cost.gold = Math.max(0, cost.gold - discount);
        player.nextPurchaseDiscount = 0;
        console.log(`[PURCHASE] nextPurchaseDiscount: -${discount} złota (${cost.gold} total)`);
      }

      if (!ResourceManager.canAffordFromPoolAndBank(player, cost)) {
        return {
          success: false,
          error: 'Niewystarczające zasoby',
          required: cost,
          available: {
            pool: player.pool || { strength: 0, knowledge: 0, gold: 0 },
            bank: player.resources
          }
        };
      }

      // 5. Wydaj zasoby (najpierw z puli, potem z banku)
      ResourceManager.spendFromPoolOrBank(player, cost);

      // 6. Usuń kartę z Targowiska — slot zostaje pusty na swoim miejscu (jak w backendzie),
      // a refill wypełni go w tym samym polu. Karty nigdy się nie przesuwają.
      game.marketplace[cardIndex] = null;

      // 7. Dodaj kartę do odrzutu gracza
      player.discard.push(cardId);

      // 7a. Dodaj kartę do odrzutu targowiska — żeby talia mogła się przetasować gdy się skończy
      if (!game.marketDiscard) game.marketDiscard = [];
      game.marketDiscard.push(cardId);

      // 7b. Oznacz że gracz dokonał zakupu w tej turze (dla pasywnych umiejętności)
      player.purchasedThisTurn = true;

      // 7b. Zwiększ licznik nabytych kart (dla efektów jak "Wiek Odkryć")
      player.cardsAcquiredThisTurn = (player.cardsAcquiredThisTurn || 0) + 1;

      // 8. NIE uzupełniaj Targowiska - zostanie uzupełnione na końcu tury
      // Pozostaw puste miejsce aż do Fazy Końcowej

      // 9. Zużyj akcję - najpierw dodatkowy zakup, potem główna akcja
      if ((player.extraPurchases || 0) > 0) {
        player.extraPurchases--;
        // NIE zużywa głównej akcji
      } else if (player.spendMainAction) {
        player.spendMainAction();
      } else if (player.bonusActionsThisTurn > 0) {
        player.bonusActionsThisTurn -= 1;
      } else {
        player.mainActionsRemaining -= 1;
      }

      // 10. Log
      game.logEvent(
        player.userId.toString(),
        player.username,
        'card_purchased',
        {
          cardId,
          cardName: card.name,
          cost,
          remainingPurchases: player.purchasesRemaining
        }
      );

      return {
        success: true,
        card: { id: cardId, name: card.name },
        cost,
        remainingResources: { ...player.resources },
        remainingPurchases: player.purchasesRemaining
      };
    } catch (error) {
      console.error('Błąd zakupu karty:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * AKCJA: Wyzwanie na Szlaku (Odwagi lub Mądrości)
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @param {String} pathType - 'courage' lub 'wisdom'
   * @param {Number} cardIndex - Indeks karty na szlaku (0-2)
   * @returns {Object} - Status operacji
   */
  static async challengePath(game, player, pathType, cardIndex, options = {}) {
    try {
      // paySurcharge — decyzja gracza z modalu przed Wyzwaniem: dopłacić za Dar czy przyjąć Ofiarę.
      const { paySurcharge = false } = options;
      // 1. Sprawdź czy gracz ma akcję główną lub dodatkowe wyzwanie
      // (extraChallengeQuestOrTrap sprawdzamy po pobraniu karty, bo wymaga znajomości typu)
      const hasActions = (player.extraChallenges || 0) > 0 ||
        (player.extraChallengeQuestOrTrap || 0) > 0 ||
        (player.hasActionsRemaining ? player.hasActionsRemaining() : (player.mainActionsRemaining + player.bonusActionsThisTurn) > 0);
      if (!hasActions) {
        return { success: false, error: 'Brak dostępnych akcji głównych' };
      }

      // 2. Wybierz odpowiedni szlak
      const path = pathType === 'courage' ? game.pathOfCourage : game.pathOfWisdom;
      const refillMethod = pathType === 'courage' ? 'refillPathOfCourage' : 'refillPathOfWisdom';

      if (!path || cardIndex < 0 || cardIndex >= path.length) {
        return { success: false, error: 'Nieprawidłowy indeks karty' };
      }

      const cardId = path[cardIndex];
      const card = await this.getCardById(cardId);

      if (!card) {
        return { success: false, error: 'Nie znaleziono karty w bazie' };
      }

      // 3. Sprawdź koszt wyzwania (pula + bank) z uwzględnieniem efektów wydarzeń
      let cost = { ...this.normalizeCardCost(card) };

      // Podbicia wydarzeniem NIE wchodzą do kosztu — działają jak Potężny Cios: kartę można
      // pokonać za wartość podstawową, ale wtedy dochodzi Ofiara. Lustro backendu.
      const surchargeParts = { gate: 0, event: 0 };
      if (game.activeDurationEffects && game.activeDurationEffects.length > 0) {
        if (pathType === 'courage' && (card.type === 'monster' || card.type === 'epic_boss')) {
          surchargeParts.event += game.getDurationEffectTotal('MONSTERS_HARDER') || 0;
        }
        if (pathType === 'wisdom' && ['trap', 'quest', 'epic_quest'].includes(card.type)) {
          surchargeParts.event += game.getDurationEffectTotal('QUESTS_AND_TRAPS_HARDER') || 0;
        }
      }

      if (!ResourceManager.canAffordFromPoolAndBank(player, cost)) {
        return {
          success: false,
          error: 'Niewystarczające zasoby do wyzwania',
          required: cost,
          available: {
            pool: player.pool || { strength: 0, knowledge: 0, gold: 0 },
            bank: player.resources
          }
        };
      }

      // === DOPŁATA: jedna kwota, jedna decyzja (Dar albo Ofiara) ===
      surchargeParts.gate = getChallengeGateSurcharge(card, pathType);
      const surchargeAmount = Object.values(surchargeParts).reduce((sum, v) => sum + (v || 0), 0);
      const surchargeResource = pathType === 'wisdom' ? 'knowledge' : 'strength';
      let surchargePaid = false;
      if (surchargeAmount > 0 && paySurcharge) {
        const available = (player.pool?.[surchargeResource] || 0) + (player.resources?.[surchargeResource] || 0);
        surchargePaid = (available - (cost[surchargeResource] || 0)) >= surchargeAmount;
      }

      // 4. Wydaj zasoby (najpierw z puli, potem z banku)
      ResourceManager.spendFromPoolOrBank(player, cost);
      if (surchargePaid) {
        ResourceManager.spendFromPoolOrBank(player, { [surchargeResource]: surchargeAmount });
      }

      // 5. Usuń kartę ze szlaku — slot zostaje PUSTY na swoim miejscu (karty się nie
      // przesuwają), a wypełni go dopiero refill na koniec tury. Spójne z backendem.
      path[cardIndex] = null;

      // 6. Dodaj kartę do TROFEÓW gracza (dla punktacji/sławy).
      // Trofeum to JEDYNE miejsce docelowe pokonanej karty — dawniej karta szła też
      // do odrzutu Szlaku „do recyklingu", przez co wracała do talii mimo zabrania jej
      // przez gracza i Szlak nigdy nie mógł się realnie wyczerpać (klon karty w obiegu).
      if (!player.trophies) {
        player.trophies = [];
      }
      player.trophies.push(cardId);
      console.log(`[CHALLENGE] Karta ${card.name} → trofea gracza ${player.username} (wyjęta z obiegu)`);

      // NOWE: Śledź liczbę pokonanych potworów w tej turze
      if (card.type === 'monster' || card.type === 'epic_boss' || (card.type === 'monster' && card.subtype === 'boss')) {
        player.monstersDefeatedThisTurn = (player.monstersDefeatedThisTurn || 0) + 1;

        // Horda Pustkowi "Łowca Trofeów": track strong monster defeats (>4 strength)
        if (player.faction === 'horda_pustkowi' && card.stats &&
            (card.stats.strength > 4 || card.stats.requiredStrength > 4)) {
          player.strongMonstersDefeatedThisTurn = (player.strongMonstersDefeatedThisTurn || 0) + 1;

          if (player.strongMonstersDefeatedThisTurn === 2) {
            const validOptions = await this.getValidOptionsForEffect(game, player, 'TAKE_FREE_MARKET_CARD', { maxCost: 6 });
            if (validOptions.length > 0) {
              player.pendingEffects.push({
                type: 'TAKE_FREE_MARKET_CARD',
                source: 'hero_passive',
                sourceCardId: 'hero_horda_pustkowi',
                value: 1,
                maxCost: 6,
                options: validOptions,
                description: 'Łowca Trofeów'
              });
              console.log(`[CHALLENGE] Horda Pustkowi "Łowca Trofeów" triggered - 2 strong monsters defeated`);
            }
          }
        }
      }

      // 6a. Aktualizuj globalne liczniki wyzwań epickich
      if (card.type === 'monster' && card.subtype === 'boss') {
        game.bossesDefeated = (game.bossesDefeated || 0) + 1;
        console.log(`[CHALLENGE] Boss pokonany! Łącznie: ${game.bossesDefeated}`);
      } else if (card.type === 'epic_quest') {
        game.epicQuestsCompleted = (game.epicQuestsCompleted || 0) + 1;
        console.log(`[CHALLENGE] Epickie zadanie ukończone! Łącznie: ${game.epicQuestsCompleted}`);
      }

      // Sprawdź natychmiastowy koniec gry
      const gameEnded = game.checkGameEnd();
      if (gameEnded) {
        game.calculateWinner();
        console.log(`[CHALLENGE] Gra zakończona! Zwycięzca: ${game.winner}`);
      }

      // 6b. Zastosuj nagrody za pokonanie karty: WYŁĄCZNIE Róg (zasoby) i Chwała.
      // Dar (efekt z panelu opisu) należy się tylko za opłaconą dopłatę. Lustro backendu.
      const { corner, fame, gift } = splitCardRewards(card, OFFLINE_PENALTY_TYPES);
      const effectsToApply = [...corner, ...fame];
      const appliedRewards = []; // NOWE: Śledzenie zastosowanych nagród dla logu
      if (surchargePaid && gift.length > 0) effectsToApply.push(...gift);

      if (effectsToApply.length > 0) {
        console.log(`[CHALLENGE] Aplikowanie ${effectsToApply.length} efektów z karty ${card.name}`);
        for (const effect of effectsToApply) {
          // Handle ALL_PLAYERS effects - apply to every player
          if (effect.type === 'ALL_PLAYERS_OPTIONAL_DESTROY_FROM_DISCARD') {
            for (const p of game.players) {
              if (!p.isActive) continue;
              const discardOptions = (p.discard || []).filter(cId => cId && !cId.startsWith('negative_') && !cId.startsWith('curse_'));
              if (discardOptions.length > 0) {
                if (!p.pendingEffects) p.pendingEffects = [];
                p.pendingEffects.push({
                  type: 'DESTROY_FROM_DISCARD',
                  value: effect.value || 1,
                  source: 'discard',
                  cardId: cardId,
                  options: discardOptions,
                  description: effect.description || 'Możesz zniszczyć 1 kartę ze stosu odrzuconych.'
                });
                console.log(`[CHALLENGE] ALL_PLAYERS: Added OPTIONAL_DESTROY_FROM_DISCARD to ${p.username} (${discardOptions.length} options)`);
              }
            }
            continue;
          }

          // Pomiń efekty wymagające wyboru - te będą obsłużone przez pendingEffects
          if (!CardEffects.requiresPlayerChoice(effect.type)) {
            const effectResult = await CardEffects.applyEffect(game, player, effect, {});
            if (effectResult.success) {
              console.log(`[CHALLENGE] Efekt ${effect.type} zastosowany:`, effectResult.details);
              // Dodaj do listy zastosowanych nagród dla logu
              appliedRewards.push({
                type: effect.type,
                value: effect.value,
                result: effectResult.details
              });
            } else {
              console.log(`[CHALLENGE] Efekt ${effect.type} nieudany:`, effectResult.error);
            }
          } else {
            // Efekt wymaga wyboru - dodaj do pendingEffects
            const source = this.getEffectSource(effect.type);
            const options = await this.getValidOptionsForEffect(game, player, effect.type, effect);

            if (options && options.length > 0) {
              if (!player.pendingEffects) {
                player.pendingEffects = [];
              }
              const pendingEffectData = {
                type: effect.type,
                value: effect.value || effect.max || 1,
                source: source,
                cardId: cardId,
                options: options
              };

              // Pass maxCost through for TAKE_FREE_MARKET_CARD
              if (effect.maxCost) {
                pendingEffectData.description = `maxCost:${effect.maxCost}`;
              }

              // Dla PEEK_AND_DISCARD_DECK - zapisz ID wierzchniej karty w value i description
              if (effect.type === 'PEEK_AND_DISCARD_DECK' && player.deck && player.deck.length > 0) {
                const topCardId = player.deck[0];
                pendingEffectData.value = topCardId;
                pendingEffectData.description = topCardId;
              }

              player.pendingEffects.push(pendingEffectData);
              console.log(`[CHALLENGE] Efekt ${effect.type} wymaga wyboru - dodano do pendingEffects`);
            } else {
              console.log(`[CHALLENGE] Skipping choice effect ${effect.type} - no valid options available.`);
            }
          }
        }
      }

      // 6c. Sprawdź bonus złota za pokonanie potwora (Zasadzka, Zabójca)
      if (pathType === 'courage' && (card.type === 'monster' || card.type === 'epic_boss' || (card.type === 'monster' && card.subtype === 'boss'))) {
        const monsterGoldBonus = player.monsterDefeatBonusGold || 0;
        if (monsterGoldBonus > 0) {
          ResourceManager.addToPool(player, { gold: monsterGoldBonus });
          console.log(`[CHALLENGE] Monster defeat bonus gold: +${monsterGoldBonus} from card effects`);
          // Dodaj bonus złota do zastosowanych nagród dla logu
          appliedRewards.push({
            type: 'BONUS_MONSTER_GOLD',
            value: monsterGoldBonus,
            result: { gold: monsterGoldBonus, addedToPool: true }
          });
        }

        // NOWE: Sprawdź efekty tymczasowe (np. Łowca Potworów)
        if (player.temporaryEffects && player.temporaryEffects.length > 0) {
          const challengeEffects = player.temporaryEffects.filter(e => e.effectType === 'EXTRA_CHALLENGE_ON_MONSTER_DEFEAT');
          if (challengeEffects.length > 0) {
            let totalBonus = 0;
            for (const effect of challengeEffects) {
              totalBonus += (effect.value || 1);
            }
            player.extraChallenges = (player.extraChallenges || 0) + totalBonus;
            console.log(`[CHALLENGE] Przyznano +${totalBonus} dodatkowych wyzwań z efektów tymczasowych`);

            // Dodaj do logu
            appliedRewards.push({
              type: 'EXTRA_CHALLENGE_GRANTED',
              value: totalBonus,
              result: { challenges: totalBonus }
            });

            // Usuwamy zużyte efekty tymczasowe
            player.temporaryEffects = player.temporaryEffects.filter(e => e.effectType !== 'EXTRA_CHALLENGE_ON_MONSTER_DEFEAT');
          }
        }
      }

      // 6a. Zastosuj bonusy/kary z aktywnych efektów wydarzeń
      const eventBonuses = { gold: 0 };
      const eventPenalties = [];

      if (game.activeDurationEffects && game.activeDurationEffects.length > 0) {
        // MONSTERS_EXTRA_GOLD: +X złota za pokonanie potwora
        if (pathType === 'courage' && (card.type === 'monster' || card.type === 'epic_boss')) {
          const extraGold = game.getDurationEffectTotal('MONSTERS_EXTRA_GOLD');
          if (extraGold > 0) {
            ResourceManager.addToPool(player, { gold: extraGold });
            eventBonuses.gold = extraGold;
            console.log(`[CHALLENGE] MONSTERS_EXTRA_GOLD: +${extraGold} złota do puli`);
            // Dodaj bonus z eventu do logu
            appliedRewards.push({
              type: 'EVENT_MONSTER_GOLD',
              value: extraGold,
              result: { gold: extraGold, addedToPool: true }
            });
          }

          // MONSTERS_GIVE_WOUND: Gracz dostaje kartę Rany
          if (game.hasActiveDurationEffect('MONSTERS_GIVE_WOUND')) {
            // Dodaj kartę Rany do odrzutu gracza
            player.discard.push('negative_rana');
            eventPenalties.push('wound');
            console.log(`[CHALLENGE] MONSTERS_GIVE_WOUND: Gracz otrzymał kartę Rany`);
          }
        }
      }

      // 6d. OFIARA — dopłata była wymagana, ale gracz jej nie wniósł.
      // Dopłata 0 (karta bez bramki i bez podbicia) nie karze wcale.
      if (surchargeAmount > 0 && !surchargePaid) {
        await this.applyCardPenalty(game, player, card, {
          sourceCardId: cardId,
          fallbackCardId: surchargeResource === 'knowledge' ? 'negative_wstyd' : 'negative_rana'
        });
      }

      // 7. NIE uzupełniaj szlaku - zostanie uzupełniony na końcu tury
      // Pozostaw puste miejsce aż do Fazy Końcowej

      // 8. Zużyj akcję - najpierw dodatkowe wyzwanie, potem główna akcja
      const isQuestOrTrap = card.type === 'quest' || card.type === 'epic_quest' || card.type === 'trap';
      if ((player.extraChallenges || 0) > 0) {
        player.extraChallenges--;
        // NIE zużywa głównej akcji
      } else if (isQuestOrTrap && (player.extraChallengeQuestOrTrap || 0) > 0) {
        player.extraChallengeQuestOrTrap--;
        // NIE zużywa głównej akcji - dodatkowe wyzwanie tylko na quest/trap
      } else if (player.spendMainAction) {
        player.spendMainAction();
      } else if (player.bonusActionsThisTurn > 0) {
        player.bonusActionsThisTurn -= 1;
      } else {
        player.mainActionsRemaining -= 1;
      }

      // 9. Log
      game.logEvent(
        player.userId.toString(),
        player.username,
        'path_challenged',
        {
          pathType,
          cardId,
          cardName: card.name,
          cost,
          appliedRewards, // NOWE: Przekaż listę nagród do logu
          eventBonuses,
          eventPenalties,
          remainingChallenges: player.challengesRemaining
        }
      );

      return {
        success: true,
        pathType,
        card: { id: cardId, name: card.name, type: card.type },
        cost,
        eventBonuses,
        eventPenalties,
        remainingResources: { ...player.resources },
        remainingChallenges: player.challengesRemaining
      };
    } catch (error) {
      console.error('Błąd wyzwania:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * AKCJA: Wyprawa w Ciemno - Phase 1: Dobierz karty z talii
   * Kosztuje 1 akcję główną. Standardowo odsłania 1 kartę Męstwa i 1 Mądrości;
   * synergie (`VENTURE_DRAW_BONUS`) podbijają tę liczbę — patrz `ventureDrawCounts`.
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @returns {Object} - { success, drawnCourageIds, drawnWisdomIds, drawnCourageCards, drawnWisdomCards }
   */
  static async startWyprawaWCiemno(game, player) {
    try {
      // Bez Wytchnienia: limit Wypraw w Nieznane (max 2)
      if (game?.gameMode === 'relentless') {
        if ((player.expeditionsCount || 0) >= 2) {
          return { success: false, error: 'Osiągnąłeś maksymalny limit 2 Wypraw w Nieznane w trybie Bez Wytchnienia' };
        }
      }

      // 1. Sprawdź akcję główną
      const hasActions = player.hasActionsRemaining
        ? player.hasActionsRemaining()
        : (player.mainActionsRemaining + (player.bonusActionsThisTurn || 0)) > 0;
      if (!hasActions) {
        return { success: false, error: 'Brak dostępnych akcji głównych' };
      }

      // 2. Sprawdź czy jest już aktywna wyprawa
      if (game.wyprawaWCiemno?.active) {
        return { success: false, error: 'Wyprawa w ciemno jest już aktywna' };
      }

      // 3. Upewnij się że talie mają karty (dolosuj z discard jeśli trzeba)
      if (game.courageDeck.length === 0) {
        game.reshuffleCourageDeck();
      }
      if (game.wisdomDeck.length === 0) {
        game.reshuffleWisdomDeck();
      }

      if (game.courageDeck.length === 0) {
        return { success: false, error: 'Talia Ścieżki Męstwa jest pusta' };
      }
      if (game.wisdomDeck.length === 0) {
        return { success: false, error: 'Talia Ścieżki Mądrości jest pusta' };
      }

      // 4. Odsłoń karty — po ile z każdej talii mówi `ventureDrawBonus` gracza.
      //    Talia może nie mieć tylu kart nieeventowych; wtedy odsłaniamy tyle, ile jest,
      //    ale przynajmniej po jednej z każdej Ścieżki (inaczej Wyprawa się nie odbywa).
      const drawCounts = ventureDrawCounts(player);

      const courageDraw = takeNonEventCards(game.courageDeck, drawCounts.courage);
      if (courageDraw.taken.length === 0) {
        return { success: false, error: 'Brak dostępnych kart Ścieżki Męstwa' };
      }

      const wisdomDraw = takeNonEventCards(game.wisdomDeck, drawCounts.wisdom);
      if (wisdomDraw.taken.length === 0) {
        restoreDrawnCards(game.courageDeck, courageDraw.removals);
        return { success: false, error: 'Brak dostępnych kart Ścieżki Mądrości' };
      }

      const drawnCourageIds = courageDraw.taken;
      const drawnWisdomIds = wisdomDraw.taken;
      const drawnCourageCards = await Promise.all(drawnCourageIds.map(id => this.getCardById(id)));
      const drawnWisdomCards = await Promise.all(drawnWisdomIds.map(id => this.getCardById(id)));

      if (drawnCourageCards.some(c => !c) || drawnWisdomCards.some(c => !c)) {
        // Cofnij jeśli którakolwiek karta nie została znaleziona
        restoreDrawnCards(game.courageDeck, courageDraw.removals);
        restoreDrawnCards(game.wisdomDeck, wisdomDraw.removals);
        return { success: false, error: 'Nie znaleziono kart w bazie' };
      }

      // 5. Zapisz stan wyprawy w grze
      game.wyprawaWCiemno = {
        active: true,
        drawnCourageIds,
        drawnWisdomIds,
        playerId: player.userId.toString()
      };

      // Increment expeditionsCount
      player.expeditionsCount = (player.expeditionsCount || 0) + 1;

      // 6. Zużyj akcję główną
      if (player.spendMainAction) {
        player.spendMainAction();
      } else if (player.bonusActionsThisTurn > 0) {
        player.bonusActionsThisTurn -= 1;
      } else {
        player.mainActionsRemaining -= 1;
      }

      game.logEvent(
        player.userId.toString(),
        player.username,
        'wyprawa_w_ciemno_start',
        { drawnCourageIds, drawnWisdomIds }
      );

      return {
        success: true,
        drawnCourageIds,
        drawnWisdomIds,
        drawnCourageCards,
        drawnWisdomCards
      };
    } catch (error) {
      console.error('[startWyprawaWCiemno] Błąd:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Migawka stanu graczy — sprawdza, czy Kara cokolwiek zmieniła.
   * Lustro `GameEngine.penaltyStateSignature` z backendu.
   */
  static penaltyStateSignature(game) {
    return (game.players || []).map(p => [
      (p.discard || []).length,
      (p.hand || []).length,
      (p.deck || []).length,
      (p.pendingEffects || []).length,
      p.fame || 0,
      p.mainActionsRemaining || 0,
      (p.resources?.gold || 0) + (p.resources?.strength || 0) + (p.resources?.knowledge || 0),
      (p.pool?.gold || 0) + (p.pool?.strength || 0) + (p.pool?.knowledge || 0),
      Object.values(p.equippedArtifacts || {}).filter(Boolean).length
    ].join(':')).join('|');
  }

  /**
   * Rozpatruje Karę wydrukowaną na karcie Przygody.
   * Lustro `GameEngine.applyCardPenalty` z backendu — patrz tam po opis zasady.
   */
  static async applyCardPenalty(game, player, card, options = {}) {
    const {
      sourceCardId = card?.id,
      respectAegis = true,
      fallbackCardId = 'negative_rana'
    } = options;

    // Czytamy EFEKT `IGNORE_PENALTY` z założonych Artefaktów, nie konkretne id karty —
    // Aegis Nieśmiertelnych wypadł w Fazie 1, a jego efekt przejął Pancerz z Łusek Smoka
    // (patrz plan/artefakty-symetria-6-6-6.md). Odpowiednik online:
    // ArtifactEffectProcessor.hasIgnorePenalty.
    if (respectAegis && player.equippedArtifacts) {
      const equippedIds = Object.values(player.equippedArtifacts).filter(Boolean);
      const equippedCards = await Promise.all(equippedIds.map((id) => this.getCardById(id)));
      const ignores = equippedCards.some((c) =>
        (c?.effects || []).some((e) => e.effect === 'IGNORE_PENALTY'));
      if (ignores) {
        console.log(`[applyCardPenalty] IGNORE_PENALTY: kara z ${card?.name || sourceCardId} zignorowana`);
        return { applied: 0, blockedByAegis: true, negativeCards: [] };
      }
    }

    const penaltyEffects = (card?.penalty && card.penalty.length)
      ? card.penalty
      : (card?.effects || []).filter(e => OFFLINE_PENALTY_TYPES.includes(e.type));

    // Karta z Karą „Brak" (typ NONE) naprawdę nie karze — nie podstawiamy nic w zamian.
    const declaredNoPenalty = penaltyEffects.length > 0 && penaltyEffects.every(e => e?.type === 'NONE');

    player.discard = player.discard || [];
    const discardBefore = player.discard.length;
    const signatureBefore = this.penaltyStateSignature(game);

    for (const effect of penaltyEffects) {
      if (effect?.type === 'NONE') continue;
      if (!CardEffects.requiresPlayerChoice(effect.type)) {
        await CardEffects.applyEffect(game, player, effect, {});
      } else {
        const effectOptions = await this.getValidOptionsForEffect(game, player, effect.type, effect);
        if (effectOptions && effectOptions.length > 0) {
          if (!player.pendingEffects) player.pendingEffects = [];
          player.pendingEffects.push({
            type: effect.type,
            value: effect.value || effect.max || 1,
            source: this.getEffectSource(effect.type),
            cardId: sourceCardId,
            options: effectOptions
          });
        }
      }
    }

    if (!declaredNoPenalty && this.penaltyStateSignature(game) === signatureBefore) {
      player.discard.push(fallbackCardId);
      console.log(`[applyCardPenalty] ${card?.name || sourceCardId}: Kara nic nie zmieniła, domyślna kara ${fallbackCardId}`);
    }

    const negativeCards = player.discard.slice(discardBefore)
      .filter(id => typeof id === 'string' && (id.startsWith('negative_') || id.startsWith('curse_')));

    return { applied: penaltyEffects.length, blockedByAegis: false, negativeCards };
  }

  /**
   * AKCJA: Wyprawa w Ciemno - Phase 2: Rozwiąż wybór gracza
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @param {String} chosenCardId - ID wybranej karty (courage lub wisdom)
   * @returns {Object} - { success, canAfford, card, ... }
   */
  static async resolveWyprawaWCiemno(game, player, chosenCardId) {
    try {
      // 1. Sprawdź stan wyprawy
      if (!game.wyprawaWCiemno?.active) {
        return { success: false, error: 'Brak aktywnej wyprawy w ciemno' };
      }

      const { playerId } = game.wyprawaWCiemno;
      const drawnCourageIds = [...(game.wyprawaWCiemno.drawnCourageIds || [])];
      const drawnWisdomIds = [...(game.wyprawaWCiemno.drawnWisdomIds || [])];

      // 2. Sprawdź czy to właściwy gracz
      if (playerId !== player.userId.toString()) {
        return { success: false, error: 'To nie twoja wyprawa' };
      }

      // 3. Sprawdź czy chosen należy do wylosowanych kart
      const isCourageChoice = drawnCourageIds.includes(chosenCardId);
      const isWisdomChoice = drawnWisdomIds.includes(chosenCardId);
      if (!isCourageChoice && !isWisdomChoice) {
        return { success: false, error: 'Wybrana karta nie należy do wyprawy' };
      }

      const chosenCard = await this.getCardById(chosenCardId);
      if (!chosenCard) {
        return { success: false, error: 'Nie znaleziono wybranej karty' };
      }

      // 4. Ustal ścieżkę wybranej karty
      const chosenPathType = isCourageChoice ? 'courage' : 'wisdom';

      // 5. Sprawdź koszt i affordability
      let cost = { ...this.normalizeCardCost(chosenCard) };
      // Konwersja siła→wiedza dla potworów/bossów na Ścieżce Mądrości (spójnie z challengePath)
      if (chosenPathType === 'wisdom' &&
          (chosenCard.type === 'monster' || chosenCard.type === 'epic_boss') &&
          cost.strength !== undefined) {
        cost = { knowledge: cost.strength };
      }
      const canAfford = ResourceManager.canAffordFromPoolAndBank(player, cost);


      if (canAfford) {
        // === SUKCES: Gracz może sobie pozwolić na kartę ===

        // 5a. Wydaj zasoby
        ResourceManager.spendFromPoolOrBank(player, cost);

        // 5b. Dodaj do trofeów
        if (!player.trophies) player.trophies = [];
        player.trophies.push(chosenCardId);
        // Śledź trofea z Wypraw w Nieznane osobno (kontrakt Mistrzowska Wyprawa)
        if (!player.ventureTrophies) player.ventureTrophies = [];
        player.ventureTrophies.push(chosenCardId);

        // 5c. Wszystkie odsłonięte karty trafiają do dyskardu swojej ścieżki
        game.courageDiscard = game.courageDiscard || [];
        game.wisdomDiscard = game.wisdomDiscard || [];
        game.courageDiscard.push(...drawnCourageIds);
        game.wisdomDiscard.push(...drawnWisdomIds);

        // 5d. Daj 2 punkty sławy (bonus za wygranie Wyprawy)
        player.fame = (player.fame || 0) + 2;
        console.log(`[resolveWyprawaWCiemno] +2 sławy dla ${player.username} (wyprawa w ciemno sukces)`);

        // 5d'. DOPŁATA (Potężny Cios / Skupiony Umysł). Tryb offline nie oferuje jeszcze
        // wyboru, więc gracz zawsze przyjmuje Ofiarę wydrukowaną na karcie.
        // Udana Wyprawa Ofiary nie znosi — Dar i Ofiara mogą wypaść razem.
        const ventureSurcharge = getChallengeGateSurcharge(chosenCard, chosenPathType);
        if (ventureSurcharge > 0) {
          await this.applyCardPenalty(game, player, chosenCard, {
            sourceCardId: chosenCardId,
            fallbackCardId: chosenPathType === 'wisdom' ? 'negative_wstyd' : 'negative_rana'
          });
        }

        // 5e. Zastosuj nagrody wybranej karty. Udana Wyprawa jest wyzwalaczem Daru,
        // więc idą wszystkie trzy sloty: Róg, Chwała i Dar.
        const ventureSlots = splitCardRewards(chosenCard, OFFLINE_PENALTY_TYPES);
        const effectsToApply = [...ventureSlots.corner, ...ventureSlots.fame, ...ventureSlots.gift];

        for (const effect of effectsToApply) {
          if (!CardEffects.requiresPlayerChoice(effect.type)) {
            await CardEffects.applyEffect(game, player, effect, {});
          } else {
            const options = await this.getValidOptionsForEffect(game, player, effect.type, effect);
            if (options && options.length > 0) {
              if (!player.pendingEffects) player.pendingEffects = [];
              player.pendingEffects.push({
                type: effect.type,
                value: effect.value || effect.max || 1,
                source: this.getEffectSource(effect.type),
                cardId: chosenCardId,
                options
              });
            }
          }
        }

        // 5f. Aktualizuj liczniki bossów/epickich
        if (chosenCard.type === 'monster' && chosenCard.subtype === 'boss') {
          game.bossesDefeated = (game.bossesDefeated || 0) + 1;
        } else if (chosenCard.type === 'epic_quest') {
          game.epicQuestsCompleted = (game.epicQuestsCompleted || 0) + 1;
        }

        game.checkGameEnd();
        if (game.gameEnded) game.calculateWinner();

        game.logEvent(player.userId.toString(), player.username, 'wyprawa_w_ciemno_success', {
          chosenCardId, chosenPathType, cost, fame: 2
        });

      } else {
        // === PORAŻKA: Gracz nie ma środków ===

        // 5a. Wszystkie odsłonięte karty trafiają do dyskardu
        game.courageDiscard = game.courageDiscard || [];
        game.wisdomDiscard = game.wisdomDiscard || [];
        game.courageDiscard.push(...drawnCourageIds);
        game.wisdomDiscard.push(...drawnWisdomIds);

        // 5b. Zastosuj Karę z wybranej karty
        const penaltyResult = await this.applyCardPenalty(game, player, chosenCard, {
          sourceCardId: chosenCardId,
          fallbackCardId: chosenPathType === 'wisdom' ? 'negative_wstyd' : 'negative_rana'
        });

        game.logEvent(player.userId.toString(), player.username, 'wyprawa_w_ciemno_fail', {
          chosenCardId, chosenPathType, penaltiesApplied: penaltyResult.applied
        });
      }

      // 6. Wyczyść stan wyprawy
      game.wyprawaWCiemno = {
        active: false,
        drawnCourageIds: [],
        drawnWisdomIds: [],
        playerId: null
      };
      if (typeof game.markModified === 'function') {
        game.markModified('wyprawaWCiemno');
      }

      return {
        success: true,
        canAfford,
        card: { id: chosenCardId, name: chosenCard.name, type: chosenCard.type },
        chosenPathType,
        cost
      };
    } catch (error) {
      console.error('[resolveWyprawaWCiemno] Błąd:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * AKCJA: Podjęcie Zlecenia (Quest)
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @returns {Object} - Status operacji
   */
  static async takeQuest(game, player) {
    try {
      // 1. Sprawdź czy gracz ma akcję główną
      if (!player.hasActionsRemaining || !player.hasActionsRemaining()) {
        return { success: false, error: 'Brak dostępnych akcji głównych' };
      }

      // 2. Sprawdź czy są zlecenia w talii
      if (game.questDeck.length === 0) {
        return { success: false, error: 'Brak dostępnych zleceń' };
      }

      // 3. Dobierz zlecenie
      const questId = game.questDeck.shift();
      const quest = await this.getCardById(questId);

      if (!quest) {
        return { success: false, error: 'Nie znaleziono zlecenia w bazie' };
      }

      // 4. Dodaj zlecenie do ręki gracza
      player.hand.push(questId);

      // 5. Zużyj akcję główną
      if (player.spendMainAction) {
        player.spendMainAction();
      } else {
        player.mainActionsRemaining -= 1;
      }

      // 6. Log
      game.logEvent(
        player.userId.toString(),
        player.username,
        'quest_taken',
        {
          questId,
          questName: quest.name,
          difficulty: quest.difficulty,
          remainingActions: player.mainActionsRemaining + player.bonusActionsThisTurn
        }
      );

      return {
        success: true,
        quest: { id: questId, name: quest.name, difficulty: quest.difficulty },
        remainingActions: player.mainActionsRemaining + player.bonusActionsThisTurn
      };
    } catch (error) {
      console.error('Błąd podjęcia zlecenia:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * AKCJA: Zagraj kartę z ręki
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @param {String} cardId - ID karty do zagrania
   * @returns {Object} - Status operacji
   */
  static async playCard(game, player, cardId) {
    try {
      // 1. Sprawdź czy karta jest w ręce
      const cardIndex = player.hand.indexOf(cardId);
      if (cardIndex === -1) {
        return { success: false, error: 'Karta nie znajduje się w ręce' };
      }

      // 2. Pobierz dane karty
      const card = await this.getCardById(cardId);
      if (!card) {
        return { success: false, error: 'Nie znaleziono karty w bazie' };
      }

      // --- ARTIFACT HANDLING (SLOT-BASED SYSTEM) ---
      if (card.type === 'artifact') {
        const category = card.category; // 'Broń', 'Pancerz', 'Amulet'
        const slotKey = this.mapCategoryToSlot(category); // 'bron', 'pancerz', 'amulet'

        console.log(`[playCard] Artifact attempt: ${card.name} (${card.id})`);
        console.log(`[playCard] Category: "${category}" -> Slot: "${slotKey}"`);
        console.log(`[playCard] Player equippedArtifacts:`, JSON.stringify(player.equippedArtifacts));

        // Inicjalizuj equippedArtifacts jeśli nie istnieje
        if (!player.equippedArtifacts) {
          player.equippedArtifacts = { pancerz: null, bron: null, amulet: null };
        }

        const currentArtifact = player.equippedArtifacts[slotKey];
        console.log(`[playCard] Current artifact in slot ${slotKey}:`, currentArtifact);

        if (currentArtifact !== null) {
           // Slot zajęty - wymagany wybór
           // WAŻNE: Używamy przypisania tablicy zamiast .push() aby Mongoose wykrył zmianę
           const newPendingEffect = {
             type: 'ARTIFACT_REPLACEMENT_CHOICE',
             source: 'equipment',
             cardId: cardId, // Nowa karta (wciąż na ręce)
             value: slotKey,  // slot do wymiany (np. 'pancerz')
             options: [cardId, currentArtifact] // [Nowa, Stara]
           };
           player.pendingEffects = [...(player.pendingEffects || []), newPendingEffect];

           console.log(`[playCard] Konflikt artefaktów w slocie ${slotKey}. Wymagany wybór.`);
           console.log(`[playCard] Added pendingEffect:`, newPendingEffect);
           console.log(`[playCard] player.pendingEffects now:`, player.pendingEffects);

           return {
             success: true,
             message: 'Wymagany wybór wymiany artefaktu',
             pendingEffects: player.pendingEffects,
             requiresChoice: true // Flag dla handlera
           };
        } else {
           // Slot wolny - załóż natychmiast
           player.hand.splice(cardIndex, 1);
           player.equippedArtifacts[slotKey] = cardId;

           // Artifact equipped - player must manually activate it this turn

           game.logEvent(
             player.userId.toString(),
             player.username,
             'artifact_equipped',
             { cardId, cardName: card.name, category, slot: slotKey }
           );

           return {
             success: true,
             card: { id: cardId, name: card.name },
             equippedArtifacts: player.equippedArtifacts,
             pendingEffects: player.pendingEffects || [],
             currentPool: player.pool ? { ...player.pool } : { strength: 0, knowledge: 0, gold: 0 },
             currentBank: { ...player.resources }
           };
        }
      }
      // --- END ARTIFACT HANDLING ---

      // 3. Usuń kartę z ręki
      player.hand.splice(cardIndex, 1);

      // 4. Dodaj kartę do zagranych
      player.played.push(cardId);

      // 5. Separuj efekty - natychmiastowe vs wymagające wyboru
      const effects = card.effects || [];
      console.log(`[playCard] Card ${card.name} has ${effects.length} effects:`, effects);
      const immediateEffects = [];
      const pendingEffectsToAdd = [];

      for (const effect of effects) {
        if (CardEffects.requiresPlayerChoice(effect.type)) {
          // Efekt wymaga wyboru - dodaj do pending
          const source = this.getEffectSource(effect.type);
          const options = await this.getValidOptionsForEffect(game, player, effect.type, effect);

          // Tylko dodaj jeśli są dostępne opcje (np. masz karty do usunięcia)
          if (options && options.length > 0) {
            pendingEffectsToAdd.push({
              type: effect.type,
              value: effect.value || effect.max || 1,
              source: source,
              cardId: cardId,
              options: options
            });
          } else {
            console.log(`[playCard] Skipping choice effect ${effect.type} - no valid options available.`);
          }
        } else {
          immediateEffects.push(effect);
        }
      }

      // 6. Zastosuj tylko efekty natychmiastowe
      const appliedEffects = [];
      for (const effect of immediateEffects) {
        const result = await CardEffects.applyEffect(game, player, effect, {});
        if (result.success) {
          appliedEffects.push({
            type: effect.type,
            value: effect.value,
            result: result.details
          });
        }
      }

      // 7. Dodaj pending effects do gracza (zapis w handlePlayCard/BotManager)
      if (pendingEffectsToAdd.length > 0) {
        if (!player.pendingEffects) {
          player.pendingEffects = [];
        }
        player.pendingEffects.push(...pendingEffectsToAdd);
      }

      console.log(`[playCard] DEBUG: pendingEffects after push:`, JSON.stringify(player.pendingEffects));
      // NIE zapisujemy tutaj - zapis odbywa się w handlePlayCard/BotManager
      // Double-save powodował utratę pendingEffects w Mongoose

      // 8. Log
      game.logEvent(
        player.userId.toString(),
        player.username,
        'card_played',
        {
          cardId,
          cardName: card.name,
          effects: appliedEffects,
          pendingEffects: pendingEffectsToAdd.map(e => e.type)
        }
      );

      return {
        success: true,
        card: { id: cardId, name: card.name },
        effects: appliedEffects,
        pendingEffects: pendingEffectsToAdd,
        currentPool: player.pool ? { ...player.pool } : { strength: 0, knowledge: 0, gold: 0 },
        currentBank: { ...player.resources },
        currentFame: player.fame
      };
    } catch (error) {
      console.error('Błąd zagrywania karty:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Pobierz źródło wyboru dla typu efektu
   * @param {String} effectType - Typ efektu
   * @returns {String} - 'marketplace', 'hand', 'discard', etc.
   */
  static getEffectSource(effectType) {
    const sourceMap = {
      // Bazar
      'TAKE_FREE_MARKET_CARD': 'marketplace',
      'TAKE_MARKET_CARD_COST_5_OR_LESS': 'marketplace',
      'DESTROY_MARKET_CARD': 'marketplace',
      'DISCARD_MARKET_CARD': 'marketplace',
      'MOVE_MARKET_CARD_TO_BOTTOM': 'marketplace',
      'TAKE_ALLY_FROM_MARKET_TO_TOP': 'marketplace',

      // Ręka
      'DISCARD_FROM_HAND': 'hand',
      'DISCARD_ARTIFACT': 'hand',
      'DESTROY_FROM_HAND': 'hand',
      'DESTROY_FROM_HAND_OPTIONAL': 'hand',
      'OPTIONAL_DESTROY_FROM_HAND': 'hand',
      'DESTROY_CARD_FROM_HAND': 'hand',
      'DESTROY_ALLY_FROM_HAND': 'hand',
      'PLACE_FROM_HAND_ON_TOP_OF_DECK': 'hand',

      // Odrzut
      'DESTROY_FROM_DISCARD': 'discard',
      'PLACE_ALLY_ON_TOP_FROM_DISCARD': 'discard',
      'RECOVER_MANEUVER_FROM_DISCARD': 'discard',

      // Ręka lub odrzut
      'DESTROY_FROM_HAND_OR_DISCARD': 'hand', // Domyślnie ręka

      // Znany Szlak
      'DISCARD_FROM_KNOWN_PATH': 'pathOfWisdom',

      // Wyposażenie
      'DESTROY_EQUIPPED_ARTIFACT': 'equipment',

      // Wybór opcji (nie karty)
      'CHOICE_GAIN_GOLD_2_OR_GAIN_GOLD_3_TAKE_SHAME': 'choice',
      'CHOICE_GAIN_STRENGTH_2_OR_GAIN_KNOWLEDGE_2': 'choice',
      'CHOICE_GAIN_STRENGTH_1_OR_GAIN_KNOWLEDGE_1': 'choice',
      'CHOICE_GAIN_STRENGTH_8_OR_GOLD_8_OR_KNOWLEDGE_8': 'choice',

      // Talia
      'PEEK_TOP_3_PLACE_IN_ORDER': 'deck',

      // Artefakty (efekty aktywne)
      'REPLACE_MARKET_CARD': 'marketplace',
      'FREE_MARKET_CARD': 'marketplace',
      'RECOVER_FROM_DISCARD': 'discard',
      'DESTROY_FOR_GOLD': 'discard',
      'PAY_FOR_EXTRA_CHALLENGE': 'choice',
      'GAIN_ANY_RESOURCE': 'choice',
      'CONVERT_RESOURCES': 'choice',
      'PEEK_AND_DISCARD_DECK': 'deck',
      'PAY_FAME_DRAW_CARDS': 'choice',
      'PLAY_FROM_DISCARD': 'discard',
      'SCRY_ADVENTURE_DECK': 'deck',

      // Efekty bohaterów (Hero abilities)
      'DESTROY_1_FROM_DISCARD': 'discard', // Lucian Ashborn
      'GAIN_2_STRENGTH_OR_2_KNOWLEDGE_TEMPORARY': 'choice', // Torbin Geargrind / Faolan
      'GAIN_4_GOLD_OR_3_STRENGTH_TEMPORARY': 'choice', // Drakon Embermaw

      // Efekt wyboru bonusu z artefaktu
      'ARTIFACT_STAT_BONUS_CHOICE': 'choice',

      // Wymiana całego targowiska (opcjonalna)
      'REPLACE_ALL_MARKET_CARDS': 'choice',

      // Odświeżenie jednej karty z targowiska (Samira)
      'REFRESH_MARKETPLACE_CARD': 'marketplace'
    };

    return sourceMap[effectType] || 'choice';
  }

  /**
   * Pobierz dostępne opcje dla efektu
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @param {String} effectType - Typ efektu
   * @returns {Array} - Lista dozwolonych opcji (cardIds lub opcje wyboru)
   */
  static async getValidOptionsForEffect(game, player, effectType, effectData = {}) {
    switch (effectType) {
      // Bazar - z opcjonalnym filtrem kosztu
      case 'TAKE_FREE_MARKET_CARD': {
        const maxCost = effectData.maxCost;
        if (maxCost) {
          const filtered = [];
          for (const id of game.marketplace) {
            const cardData = await this.getCardById(id);
            if (cardData) {
              const normalizedCost = this.normalizeCardCost(cardData);
              if ((normalizedCost.gold || 0) <= maxCost) {
                filtered.push(id);
              }
            }
          }
          return filtered;
        }
        return [...game.marketplace];
      }
      // Bazar - wszystkie karty
      case 'DESTROY_MARKET_CARD':
      case 'DISCARD_MARKET_CARD':
      case 'MOVE_MARKET_CARD_TO_BOTTOM':
        return [...game.marketplace];

      // Bazar - z filtrem kosztu
      case 'TAKE_MARKET_CARD_COST_5_OR_LESS': {
        const options = [];
        for (const id of game.marketplace) {
          const cardData = await this.getCardById(id);
          if (cardData) {
            const normalizedCost = this.normalizeCardCost(cardData);
            if ((normalizedCost.gold || 0) <= 5) {
              options.push(id);
            }
          }
        }
        return options;
      }

      // Bazar - tylko sojusznicy
      case 'TAKE_ALLY_FROM_MARKET_TO_TOP': {
        const allyOptions = [];
        for (const id of game.marketplace) {
          const cardData = await this.getCardById(id);
          if (cardData && cardData.type === 'ally') {
            allyOptions.push(id);
          }
        }
        return allyOptions;
      }

      // Ręka - wszystkie karty
      case 'DISCARD_FROM_HAND':
      case 'DESTROY_FROM_HAND':
      case 'DESTROY_FROM_HAND_OPTIONAL':
      case 'OPTIONAL_DESTROY_FROM_HAND':
      case 'DESTROY_CARD_FROM_HAND':
      case 'PLACE_FROM_HAND_ON_TOP_OF_DECK':
        return [...player.hand];

      // Ręka - tylko artefakty
      case 'DISCARD_ARTIFACT': {
        const artifactOptions = [];
        for (const id of player.hand) {
          const cardData = await this.getCardById(id);
          if (cardData && (cardData.type === 'normal_artifact' || cardData.type === 'legendary_artifact')) {
            artifactOptions.push(id);
          }
        }
        return artifactOptions;
      }

      // Ręka - tylko sojusznicy
      case 'DESTROY_ALLY_FROM_HAND': {
        const allyHandOptions = [];
        for (const id of player.hand) {
          const cardData = await this.getCardById(id);
          if (cardData && cardData.type === 'ally') {
            allyHandOptions.push(id);
          }
        }
        return allyHandOptions;
      }

      // Odrzut - wszystkie karty
      case 'DESTROY_FROM_DISCARD':
        return [...player.discard];

      // Odrzut - tylko sojusznicy
      case 'PLACE_ALLY_ON_TOP_FROM_DISCARD': {
        const allyDiscardOptions = [];
        for (const id of player.discard) {
          const cardData = await this.getCardById(id);
          if (cardData && cardData.type === 'ally') {
            allyDiscardOptions.push(id);
          }
        }
        return allyDiscardOptions;
      }

      // Odrzut - tylko manewry
      case 'RECOVER_MANEUVER_FROM_DISCARD': {
        const maneuverOptions = [];
        for (const id of player.discard) {
          const cardData = await this.getCardById(id);
          if (cardData && cardData.type === 'maneuver') {
            maneuverOptions.push(id);
          }
        }
        return maneuverOptions;
      }

      // Odrzut - Akcji lub Manewru (Księga Tajemnic)
      // "Akcja" = starter, ally; "Manewr" = maneuver. Wyklucz negative/curse.
      case 'RECOVER_FROM_DISCARD': {
        const recoverOptions = [];
        const excludedTypes = ['negative', 'curse', 'monster', 'quest', 'epic_quest', 'trap', 'event', 'artifact'];
        for (const id of player.discard) {
          const cardData = await this.getCardById(id);
          if (cardData && !excludedTypes.includes(cardData.type)) {
            recoverOptions.push(id);
          }
        }
        return recoverOptions;
      }

      // Ręka lub odrzut
      case 'DESTROY_FROM_HAND_OR_DISCARD':
        return [...player.hand, ...player.discard];

      // Znany Szlak (Wisdom + Courage)
      case 'DISCARD_FROM_KNOWN_PATH':
        const discardOptions = [];
        if (game.pathOfWisdom) discardOptions.push(...game.pathOfWisdom);
        if (game.pathOfCourage) discardOptions.push(...game.pathOfCourage);
        return discardOptions;

      // Wyposażenie
      case 'DESTROY_EQUIPPED_ARTIFACT':
        return player.equipped ? [...player.equipped] : [];

      // Wybór opcji
      case 'CHOICE_GAIN_GOLD_2_OR_GAIN_GOLD_3_TAKE_SHAME':
        return ['option1', 'option2'];

      case 'CHOICE_GAIN_STRENGTH_2_OR_GAIN_KNOWLEDGE_2':
      case 'CHOICE_GAIN_STRENGTH_1_OR_GAIN_KNOWLEDGE_1':
      case 'GAIN_2_STRENGTH_OR_2_KNOWLEDGE_TEMPORARY': // Torbin Geargrind / Faolan
        return ['strength', 'knowledge'];

      case 'CHOICE_GAIN_STRENGTH_8_OR_GOLD_8_OR_KNOWLEDGE_8': // Aspekt Bohatera
        return ['strength', 'gold', 'knowledge'];

      case 'GAIN_4_GOLD_OR_3_STRENGTH_TEMPORARY': // Drakon Embermaw
        return ['gold', 'strength'];

      // Efekty bohaterów - zniszczenie z odrzutu
      case 'DESTROY_1_FROM_DISCARD': // Lucian Ashborn
        return [...player.discard];

      // Wybór bonusu z artefaktu - opcje są budowane dynamicznie
      case 'ARTIFACT_STAT_BONUS_CHOICE':
        return ['strength', 'knowledge', 'gold'];

      // Dowolny zasób (artefakt Esencja Żywiołów) - split options
      case 'GAIN_ANY_RESOURCE':
        return ['strength', 'knowledge', 'gold', 'strength_knowledge', 'strength_gold', 'knowledge_gold'];

      // Wymiana całego targowiska (opcjonalna)
      case 'REPLACE_ALL_MARKET_CARDS':
        return ['replace', 'skip'];

      // Odświeżenie jednej karty z targowiska (Samira)
      case 'REFRESH_MARKETPLACE_CARD':
        return [...game.marketplace];

      // Darmowa karta z targowiska (po potworzeniu)
      case 'FREE_MARKET_CARD':
        return [...game.marketplace];

      // Wymiana karty z targowiska (Oko Proroka)
      case 'REPLACE_MARKET_CARD':
        return [...game.marketplace];

      // Zniszcz kartę z odrzutu za złoto
      case 'DESTROY_FOR_GOLD':
        return [...player.discard];

      // Zapłać sławę za karty / zapłać za wyzwanie
      case 'PAY_FAME_DRAW_CARDS':
        return ['confirm', 'skip'];

      case 'PAY_FOR_EXTRA_CHALLENGE':
        return ['confirm', 'skip'];

      // Scry adventure deck - top 3 karty z talii przygód
      case 'SCRY_ADVENTURE_DECK': {
        const adventureDeck = game.adventureDeck || game.wisdomDeck || game.courageDeck;
        if (adventureDeck && adventureDeck.length > 0) {
          return adventureDeck.slice(0, Math.min(3, adventureDeck.length));
        }
        return [];
      }

      // Konwersja zasobów (Kamień Filozoficzny) - opcje nie są kartami
      case 'CONVERT_RESOURCES':
        return ['convert', 'skip'];

      // Podejrzenie talii przygód - wybór ścieżki
      case 'PEEK_ADVENTURE_DECK':
        return ['wisdom', 'courage'];

      // Talia - top 3 karty
      case 'PEEK_TOP_3_PLACE_IN_ORDER':
        return player.deck.slice(0, 3);

      // Talia - podejrzyj wierzchnią kartę, wybierz: zostaw lub odrzuć
      // Jeśli talia pusta - przetasuj odrzut. options[0] = ID wierzchniej karty
      case 'PEEK_AND_DISCARD_DECK':
        if ((!player.deck || player.deck.length === 0) && player.discard && player.discard.length > 0) {
          console.log(`[PEEK_AND_DISCARD_DECK] Talia pusta - przetasowanie ${player.discard.length} kart z odrzutu`);
          const shuffled = player.shuffleArray([...player.discard]);
          player.deck.splice(0, player.deck.length, ...shuffled);
          player.discard.splice(0, player.discard.length);
        }
        if (player.deck && player.deck.length > 0) {
          return [player.deck[0], 'keep', 'discard'];
        }
        return [];

      default:
        return [];
    }
  }

  /**
   * Sprawdź czy gra może się rozpocząć (wszyscy wybrali bohaterów)
   * @param {Object} game - Obiekt gry
   * @returns {Boolean} - Czy można rozpocząć
   */
  static canStartGame(game) {
    if (!game || game.players.length < 1) return false;
    return game.players.every(p => p.heroCard && p.faction);
  }

  /**
   * Sprawdź czy na szlakach są karty wydarzeń i zwróć pierwszą znalezioną
   * @param {Object} game - Obiekt gry
   * @returns {Object|null} - Dane wydarzenia { cardId, pathType, index }
   */
  static findVisibleEvent(game) {
    console.log(`[DEBUG_EVENT] Sprawdzam szlaki w poszukiwaniu wydarzeń...`);
    console.log(`[DEBUG_EVENT] pathOfWisdom:`, game.pathOfWisdom);
    console.log(`[DEBUG_EVENT] pathOfCourage:`, game.pathOfCourage);

    // Sprawdź Szlak Mądrości
    if (game.pathOfWisdom && Array.isArray(game.pathOfWisdom)) {
      for (let i = 0; i < game.pathOfWisdom.length; i++) {
        const cardId = typeof game.pathOfWisdom[i] === 'string' ? game.pathOfWisdom[i] : game.pathOfWisdom[i]?.id;
        if (cardId && (cardId.startsWith('event_') || cardId.includes('_event_'))) {
          console.log(`[DEBUG_EVENT] Znaleziono wydarzenie na Szlaku Mądrości: ${cardId}`);
          return { cardId, pathType: 'wisdom', index: i };
        }
      }
    }

    // Sprawdź Szlak Odwagi
    if (game.pathOfCourage && Array.isArray(game.pathOfCourage)) {
      for (let i = 0; i < game.pathOfCourage.length; i++) {
        const cardId = typeof game.pathOfCourage[i] === 'string' ? game.pathOfCourage[i] : game.pathOfCourage[i]?.id;
        if (cardId && (cardId.startsWith('event_') || cardId.includes('_event_'))) {
          console.log(`[DEBUG_EVENT] Znaleziono wydarzenie na Szlaku Odwagi: ${cardId}`);
          return { cardId, pathType: 'courage', index: i };
        }
      }
    }

    console.log(`[DEBUG_EVENT] Nie znaleziono żadnych wydarzeń na szlakach.`);
    return null;
  }

  /**
   * Pobierz aktualny stan gry (dla klienta)
   * @param {Object} game - Obiekt gry
   * @param {String} userId - ID gracza (dla prywatnych danych)
   * @returns {Object} - Stan gry
   */
  static async getGameState(game, userId = null) {
    const currentPlayer = game.getCurrentPlayer();
    const player = userId ? game.players.find(p => p.userId.toString() === userId) : null;

    return {
      gameId: game._id,
      code: game.code,  // Important: Include game code
      hostId: game.hostId?.toString(),  // Important: Include host ID
      status: game.status,
      maxPlayers: game.maxPlayers || 6,
      gameConfig: game.gameConfig || {
        handSize: 5, mainActionsPerTurn: 2, purchasesPerTurn: 1,
        challengesPerTurn: 1, bankResourceLimit: 10, marketplaceSize: 5,
        pathSize: 3, epicChallengesForWin: 2, guildContractsRevealed: 3,
        maxActiveContracts: 2
      },
      currentTurn: game.currentTurn,
      currentPhase: game.currentPhase,
      bossesDefeated: game.bossesDefeated || 0,
      epicQuestsCompleted: game.epicQuestsCompleted || 0,
      currentPlayer: {
        username: currentPlayer.username,
        userId: currentPlayer.userId.toString()
      },
      publicZones: {
        marketplace: game.marketplace,
        pathOfCourage: game.pathOfCourage,
        pathOfWisdom: game.pathOfWisdom,
        cityZone: game.cityZoneCards,
        guildContracts: game.revealedGuildContracts || []
      },
      deckSizes: {
        market: game.marketDeck.length,
        courage: game.courageDeck.length,
        wisdom: game.wisdomDeck.length,
        quest: game.questDeck.length,
        guildContract: (game.guildContractDeck || []).length,
        courageDiscard: (game.courageDiscard || []).length,
        wisdomDiscard: (game.wisdomDiscard || []).length,
        // Wydarzenia zostają w odrzucie na zawsze, więc sam rozmiar odrzutu kłamie
        // o tym, ile jeszcze da się przetasować z powrotem do talii
        courageDiscardRecyclable: (game.courageDiscard || []).filter(id => !String(id).startsWith('event_')).length,
        wisdomDiscardRecyclable: (game.wisdomDiscard || []).filter(id => !String(id).startsWith('event_')).length,
      },
      // Pending event (blocking game until acknowledged)
      pendingEvent: game.pendingEvent && game.pendingEvent.cardId ? {
        cardId: game.pendingEvent.cardId,
        eventCard: await this.getCardById(game.pendingEvent.cardId), // Dodaj pełne dane karty
        triggeringPlayerId: game.pendingEvent.triggeringPlayerId,
        triggeringPlayerName: game.pendingEvent.triggeringPlayerName,
        acknowledged: game.pendingEvent.acknowledged,
        effects: game.pendingEvent.effects || [] // Dodaj efekty
      } : null,
      // Active duration effects from events
      activeDurationEffects: game.activeDurationEffects || [],
      // Wyprawa w ciemno - aktywny stan (widoczny dla wszystkich)
      wyprawaWCiemno: game.wyprawaWCiemno?.active ? {
        active: true,
        drawnCourageIds: [...(game.wyprawaWCiemno.drawnCourageIds || [])],
        drawnWisdomIds: [...(game.wyprawaWCiemno.drawnWisdomIds || [])],
        playerId: game.wyprawaWCiemno.playerId
      } : null,
      // Singleplayer mode info
      isSingleplayer: game.isSingleplayer || false,
      // Tutorial mode info
      isTutorial: game.isTutorial || false,
      tutorialMaxTurns: game.tutorialMaxTurns || 20,
      botActionLog: game.isSingleplayer ? (game.botActionLog || []) : [],
      players: game.players.map(p => ({
        username: p.username,
        userId: p.userId.toString(),
        isCurrentPlayer: p.userId.toString() === currentPlayer.userId.toString(),
        heroCard: p.heroCard || null,
        faction: p.faction || null,
        fame: p.fame,
        handSize: p.hand.length,
        deckSize: p.deck.length,
        discardSize: p.discard.length,
        playedSize: p.played.length,
        trophiesSize: p.trophies?.length || 0,  // Publiczna liczba trofeów
        equippedArtifacts: p.equippedArtifacts || { pancerz: null, bron: null, amulet: null },
        equipped: p.equipped,  // [LEGACY] dla kompatybilności
        questsCompleted: p.questsCompleted,
        resources: { ...p.resources },
        tokens: p.tokens || {}, // Publiczne tokeny
        contracts: p.contracts || [],
        expeditionsCount: p.expeditionsCount || 0,
        isBot: p.isBot || false,
        botDifficulty: p.botDifficulty || null
      })),
      // Prywatne dane gracza (jeśli podano userId)
      privateData: player ? (() => {
        console.log('[getGameState] Building privateData for player:', player.username);
        console.log('[getGameState] player.pendingEffects:', player.pendingEffects);
        return {
          hand: player.hand,
          played: player.played || [],  // Karty zagrane w tej turze
          discard: player.discard || [], // Karty w odrzucie
          trophies: player.trophies || [], // Trofea (ukończone ekspedycje)
          pool: player.pool || { strength: 0, knowledge: 0, gold: 0 },
          bank: { ...player.resources },
          tokens: player.tokens || {}, // Prywatne tokeny
          equippedArtifacts: player.equippedArtifacts || { pancerz: null, bron: null, amulet: null },
          artifactsUsedThisTurn: player.artifactsUsedThisTurn || [],
          artifactDiscount: player.artifactDiscount || 0,
          nextPurchaseDiscount: player.nextPurchaseDiscount || 0,
          actions: {
            main: player.mainActionsRemaining,
            bonus: player.bonusActionsThisTurn,
            purchases: player.purchasesRemaining,
            challenges: player.challengesRemaining,
            extraPurchases: player.extraPurchases || 0,
            extraChallenges: player.extraChallenges || 0,
            extraChallengeQuestOrTrap: player.extraChallengeQuestOrTrap || 0
          },
          // Pending effects - efekty oczekujące na wybór gracza
          pendingEffects: player.pendingEffects || []
        };
      })() : null
    };
  }
}

export default GameEngine;
